import { useState } from "react";
import { BookOpenText, Files, WarningCircle } from "@phosphor-icons/react";
import { Trans, useTranslation } from "react-i18next";
import { Badge } from "@libs/zapi-ui/ds/components/badge";
import { Button } from "@libs/zapi-ui/ds/components/button";
import { Reveal } from "@libs/zapi-ui/ds/components/reveal";
import { Spinner } from "@libs/zapi-ui/ds/components/spinner";
import { TextReveal } from "@libs/zapi-ui/ds/components/text-reveal";
import { staggerStep } from "@libs/zapi-ui/lib/motion";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import type { TProject } from "@/zapi-frontend/stores/project";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { formatUpdated } from "../../lib/formatUpdated";

type TProjectHeroProps = {
  project: TProject;
  onRefresh?: () => void;
  isRefreshing: boolean;
};

export function ProjectHero({ project, onRefresh, isRefreshing }: TProjectHeroProps) {
  const { t, i18n } = useTranslation(["projectHome", "common"]);
  const { isLabs } = useMeStore();
  const [failedCoverUrl, setFailedCoverUrl] = useState<string | null>(null);
  const coverUrl = project.logoUrl && project.logoUrl !== failedCoverUrl ? project.logoUrl : null;

  const dateLocale = i18n.language === "ja" ? "ja-JP" : "en-GB";
  const updatedLabel = formatUpdated(project.lastModified, dateLocale, (date) => t("overview.updated", { date }));
  const tags = [
    ...Object.values(project.dimensionValues ?? {}).filter(Boolean),
    project.organizationName,
    updatedLabel,
    isLabs ? project.projectTemplateName : null
  ].filter((tag): tag is string => !!tag);

  const sources = project.documents?.length ?? 0;
  const chapters = (project.projectChapters ?? []).filter((chapter) => !chapter.isHidden);
  const readyChapters = chapters.filter((chapter) => !!chapter.activeMemo).length;

  return (
    <section className="relative isolate flex flex-col gap-6">
      {coverUrl && (
        <div
          aria-hidden
          className="pointer-events-none absolute -z-10 -top-[calc(var(--app-header-height)+1rem)] -inset-x-4 aspect-[4/3] sm:aspect-[1164/513] lg:-inset-x-12"
        >
          <img
            src={coverUrl}
            alt=""
            className="cover-mask-soft absolute inset-0 size-full object-cover opacity-70 blur-2xl"
          />
          <img
            src={coverUrl}
            alt=""
            onError={() => setFailedCoverUrl(coverUrl)}
            className="cover-mask absolute inset-0 size-full object-cover"
          />
        </div>
      )}

      <div
        className={cn(
          "relative flex flex-col gap-4",
          coverUrl ? "pt-[calc(60%-4.5rem)] sm:pt-[calc(33%-4.5rem)]" : "pt-8"
        )}
      >
        <div className="flex items-end justify-between gap-4">
          <TextReveal as="h1" className="typo-h2 min-w-0 text-balance text-foreground">
            {project.name}
          </TextReveal>
          {onRefresh && (
            <div className="flex shrink-0 items-center gap-2 pb-1">
              {!isRefreshing && project.isOverviewStale && (
                <span
                  title={t("overview.outOfDateTooltip")}
                  className="typo-label-sm flex items-center gap-1 text-primary-text"
                >
                  <WarningCircle className="size-4" />
                  {t("overview.outOfDate")}
                </span>
              )}
              <Button
                type="button"
                variant="outline"
                data-testid="overview-header-refresh-button"
                className="rounded-full"
                onClick={onRefresh}
                disabled={isRefreshing}
              >
                {isRefreshing ? (
                  <>
                    <Spinner data-icon="inline-start" aria-label={t("common:loading")} />
                    {t("overview.refreshing")}
                  </>
                ) : (
                  t("overview.refresh")
                )}
              </Button>
            </div>
          )}
        </div>
        {project.shortDescription && (
          <Reveal delay={staggerStep.loose * 2}>
            <p className="typo-body-md max-w-2xl text-pretty text-muted-foreground">{project.shortDescription}</p>
          </Reveal>
        )}
        {tags.length > 0 && (
          <Reveal delay={staggerStep.loose * 2.5}>
            <ul className="flex flex-wrap gap-1.5">
              {tags.map((tag, index) => (
                <li key={index} className="min-w-0 max-w-full">
                  <Badge variant="neutral" title={tag} className="typo-caption h-6 max-w-full rounded-full px-2.5">
                    <span className="truncate">{tag}</span>
                  </Badge>
                </li>
              ))}
            </ul>
          </Reveal>
        )}
        <Reveal delay={staggerStep.loose * 3}>
          <div className="typo-label-sm flex flex-wrap items-center gap-x-4 gap-y-2 pt-1 text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <Files className="size-4" aria-hidden />
              <span>
                <Trans
                  t={t}
                  i18nKey="overview.sourceCount"
                  count={sources}
                  components={{ count: <span className="tabular-nums text-foreground" /> }}
                />
              </span>
            </span>
            <span className="flex items-center gap-1.5">
              <BookOpenText className="size-4" aria-hidden />
              <span>
                <Trans
                  t={t}
                  i18nKey="overview.chaptersReady"
                  values={{ ready: readyChapters, total: chapters.length }}
                  components={{
                    ready: <span className="tabular-nums text-foreground" />,
                    total: <span className="tabular-nums" />
                  }}
                />
              </span>
            </span>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
